import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ASSET_STATUS_LABEL, CONDITION_LABEL, dateInputToIso, toDateInput } from '../lib/format';
import { invalidateInventory, useBuildings, useCategories } from '../lib/hooks';
import { isOpenOn } from '../lib/locations';
import { modelLabel, useCatalog } from '../lib/parts';
import type { AssetBase, AssetCondition, AssetStatus } from '../lib/types';
import { useToast } from './toast';
import { Alert, Button, Field, Input, Modal, Select, Textarea } from './ui';

const EDITABLE_STATUSES: AssetStatus[] = ['AVAILABLE', 'IN_REPAIR', 'LOST', 'STOLEN', 'RETIRED', 'DISPOSED'];

/** Dropdown value that opens the inline "add to catalog" row. */
const ADD_NEW = '__new__';

type FormState = Record<
  | 'assetTag'
  | 'name'
  | 'categoryId'
  | 'status'
  | 'condition'
  | 'manufacturerId'
  | 'modelId'
  | 'serialNumber'
  | 'buildingId'
  | 'roomId'
  | 'purchaseDate'
  | 'purchasePrice'
  | 'vendor'
  | 'poNumber'
  | 'fundingSource'
  | 'warrantyExpiresAt'
  | 'notes',
  string
>;

function toForm(asset?: AssetBase | null): FormState {
  return {
    assetTag: asset?.assetTag ?? '',
    name: asset?.name ?? '',
    categoryId: asset?.categoryId ?? '',
    status: asset?.status ?? 'AVAILABLE',
    condition: asset?.condition ?? 'GOOD',
    manufacturerId: asset?.manufacturerId ?? '',
    modelId: asset?.modelId ?? '',
    serialNumber: asset?.serialNumber ?? '',
    buildingId: asset?.buildingId ?? '',
    roomId: asset?.roomId ?? '',
    purchaseDate: toDateInput(asset?.purchaseDate),
    purchasePrice: asset?.purchasePrice ?? '',
    vendor: asset?.vendor ?? '',
    poNumber: asset?.poNumber ?? '',
    fundingSource: asset?.fundingSource ?? '',
    warrantyExpiresAt: toDateInput(asset?.warrantyExpiresAt),
    notes: asset?.notes ?? '',
  };
}

function QuickAdd({
  label,
  placeholder,
  hint,
  pending,
  onAdd,
  onCancel,
}: {
  label: string;
  placeholder: string;
  hint?: string;
  pending: boolean;
  onAdd(name: string): void;
  onCancel(): void;
}) {
  const [name, setName] = useState('');
  const submit = () => name.trim() && onAdd(name.trim());

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3 sm:col-span-2">
      <p className="mb-2 text-sm font-medium text-slate-700">{label}</p>
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={name}
          maxLength={120}
          placeholder={placeholder}
          aria-label={label}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            // Keep Enter and Escape from submitting or closing the asset form.
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
            }
          }}
        />
        <Button size="sm" variant="primary" icon={<Check />} loading={pending} disabled={!name.trim()} onClick={submit}>
          Add
        </Button>
        <Button size="sm" variant="ghost" icon={<X />} onClick={onCancel} aria-label="Cancel" />
      </div>
      {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function AssetFormModal({
  open,
  onClose,
  asset,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  asset?: (AssetBase & { cart?: { name: string } | null }) | null;
  onSaved?(asset: AssetBase): void;
}) {
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const buildings = useBuildings();
  const catalog = useCatalog();
  const [form, setForm] = useState<FormState>(() => toForm(asset));
  const [adding, setAdding] = useState<'manufacturer' | 'model' | null>(null);
  const [categoryFromModel, setCategoryFromModel] = useState(false);
  const [error, setError] = useState('');
  const [loanerPool, setLoanerPool] = useState(!!asset?.loanerPool);
  // The name we filled in from the model, so picking another model can replace it.
  const autoName = useRef('');
  const editing = !!asset;
  const checkedOut = asset?.status === 'CHECKED_OUT';
  // A device on a cart is kept wherever its cart is.
  const onCart = !!asset?.cartId;

  useEffect(() => {
    if (open) {
      setForm(toForm(asset));
      setLoanerPool(!!asset?.loanerPool);
      setAdding(null);
      setCategoryFromModel(false);
      setError('');
      autoName.current = '';
    }
  }, [open, asset]);

  const set = (key: keyof FormState) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  // Closed buildings and rooms take no new devices, but a device already there keeps showing its place.
  const buildingOptions = (buildings.data ?? []).filter((building) => isOpenOn(building) || building.id === asset?.buildingId);
  const rooms = (buildings.data?.find((b) => b.id === form.buildingId)?.rooms ?? []).filter((room) => isOpenOn(room) || room.id === asset?.roomId);
  const manufacturer = catalog.data?.find((m) => m.id === form.manufacturerId) ?? null;
  const models = manufacturer?.models ?? [];
  // Older assets can carry a make or model that isn't linked to the catalog.
  const unlinked =
    editing && !asset!.modelId && (asset!.model || (!asset!.manufacturerId && asset!.manufacturer))
      ? [asset!.manufacturer, asset!.model].filter(Boolean).join(' ')
      : null;

  /** Selects a model, preselecting its category and (for new assets) naming the asset after it. */
  const applyModel = (model: { id: string; name: string; categoryId: string | null } | null, manufacturerName = manufacturer?.name) => {
    setForm((current) => {
      const next = { ...current, modelId: model?.id ?? '' };
      if (model?.categoryId) next.categoryId = model.categoryId;
      if (!editing && model && manufacturerName && (!current.name.trim() || current.name === autoName.current)) {
        next.name = modelLabel({ name: model.name, manufacturer: { name: manufacturerName } });
        autoName.current = next.name;
      }
      return next;
    });
    setCategoryFromModel(!!model?.categoryId);
  };

  const addManufacturer = useMutation({
    mutationFn: (name: string) => api.post<{ id: string; name: string }>('/manufacturers', { name }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
      setForm((current) => ({ ...current, manufacturerId: created.id, modelId: '' }));
      setAdding(null);
      toast.success(`Added ${created.name}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const addModel = useMutation({
    mutationFn: (name: string) =>
      api.post<{ id: string; name: string; categoryId: string | null }>('/models', {
        manufacturerId: form.manufacturerId,
        name,
        categoryId: form.categoryId,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['catalog'] });
      applyModel(created);
      setAdding(null);
      toast.success(`Added ${created.name}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const { manufacturerId, modelId, buildingId, roomId, ...fields } = form;
      // Only send make and model when they changed, so saving never drops an unlinked legacy value.
      const catalogChanged = !editing || manufacturerId !== (asset!.manufacturerId ?? '') || modelId !== (asset!.modelId ?? '');
      const body = {
        ...fields,
        ...(catalogChanged ? { manufacturerId, modelId } : {}),
        // A cart sets the location of the devices on it.
        ...(onCart ? {} : { buildingId, roomId }),
        assetTag: form.assetTag.trim() || undefined,
        status: checkedOut ? undefined : (form.status as AssetStatus),
        condition: form.condition as AssetCondition,
        purchaseDate: dateInputToIso(form.purchaseDate),
        warrantyExpiresAt: dateInputToIso(form.warrantyExpiresAt),
        loanerPool,
      };
      return editing ? api.patch<AssetBase>(`/assets/${asset!.id}`, body) : api.post<AssetBase>('/assets', body);
    },
    onSuccess: (saved) => {
      invalidateInventory(queryClient);
      toast.success(editing ? `Saved ${saved.assetTag}` : `Created ${saved.assetTag}`);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={editing ? `Edit ${asset!.assetTag}` : 'New asset'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="asset-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Create asset'}
          </Button>
        </>
      }
    >
      <form id="asset-form" onSubmit={submit} className="space-y-6">
        {error && <Alert tone="red">{error}</Alert>}

        <section className="grid gap-4 sm:grid-cols-2">
          <Field label="Asset tag" hint={editing ? undefined : `Leave blank to generate one (${me?.tenant?.assetTagPrefix ?? ''}0…)`}>
            <Input value={form.assetTag} onChange={set('assetTag')} maxLength={64} required={editing} className="font-mono" />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={set('name')} maxLength={200} required placeholder='e.g. MacBook Air 13"' />
          </Field>

          <Field label="Manufacturer" hint={unlinked ? `Currently “${unlinked}”, which isn’t in the catalog` : undefined}>
            <Select
              value={adding === 'manufacturer' ? ADD_NEW : form.manufacturerId}
              onChange={(e) => {
                if (e.target.value === ADD_NEW) {
                  setAdding('manufacturer');
                  return;
                }
                setAdding(null);
                setForm((current) => ({ ...current, manufacturerId: e.target.value, modelId: '' }));
                setCategoryFromModel(false);
              }}
            >
              <option value="">{catalog.isLoading ? 'Loading…' : 'Not set'}</option>
              {catalog.data?.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
              <option value={ADD_NEW}>+ Add a manufacturer…</option>
            </Select>
          </Field>
          <Field label="Model">
            <Select
              value={adding === 'model' ? ADD_NEW : form.modelId}
              disabled={!manufacturer}
              onChange={(e) => {
                if (e.target.value === ADD_NEW) {
                  setAdding('model');
                  return;
                }
                setAdding(null);
                applyModel(models.find((m) => m.id === e.target.value) ?? null);
              }}
            >
              <option value="">{manufacturer ? 'Not set' : 'Choose a manufacturer first'}</option>
              {models.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
              {manufacturer && <option value={ADD_NEW}>+ Add a model…</option>}
            </Select>
          </Field>

          {adding === 'manufacturer' && (
            <QuickAdd
              label="New manufacturer"
              placeholder="e.g. Lenovo"
              pending={addManufacturer.isPending}
              onAdd={(name) => addManufacturer.mutate(name)}
              onCancel={() => setAdding(null)}
            />
          )}
          {adding === 'model' && manufacturer && (
            <QuickAdd
              label={`New ${manufacturer.name} model`}
              placeholder="e.g. 300e Yoga Chromebook Gen 4"
              hint={
                form.categoryId
                  ? `Assets of this model will start in ${categories.data?.find((c) => c.id === form.categoryId)?.name ?? 'the selected category'}.`
                  : 'Choose a category first to make it this model’s default.'
              }
              pending={addModel.isPending}
              onAdd={(name) => addModel.mutate(name)}
              onCancel={() => setAdding(null)}
            />
          )}

          <Field label="Category" hint={categoryFromModel ? 'Preselected from the model' : undefined}>
            <Select
              value={form.categoryId}
              onChange={(e) => {
                setCategoryFromModel(false);
                set('categoryId')(e);
              }}
            >
              <option value="">Uncategorized</option>
              {categories.data?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Serial number" hint="Chromebooks link to Google by serial">
            <Input value={form.serialNumber} onChange={set('serialNumber')} maxLength={120} className="font-mono" />
          </Field>

          <Field label="Status" hint={checkedOut ? 'Check in to change' : undefined}>
            <Select value={form.status} onChange={set('status')} disabled={checkedOut}>
              {(checkedOut ? ['CHECKED_OUT' as AssetStatus] : EDITABLE_STATUSES).map((status) => (
                <option key={status} value={status}>
                  {ASSET_STATUS_LABEL[status]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Condition">
            <Select value={form.condition} onChange={set('condition')}>
              {Object.entries(CONDITION_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-sm sm:col-span-2">
            <input type="checkbox" checked={loanerPool} onChange={(e) => setLoanerPool(e.target.checked)} className="mt-0.5 size-4 rounded border-slate-300" />
            <span>
              <span className="font-medium text-slate-800">In the loaner pool</span>
              <span className="block text-xs text-slate-500">Lent while someone’s own device is repaired, or for the day, with a date it’s due back and reminders.</span>
            </span>
          </label>
        </section>

        <section>
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">Home location</h3>
          {onCart && (
            <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              On {asset?.cart?.name ?? 'a cart'}, which sets its building and room. Move the cart, or take the device off it, to change them.
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Building">
              <Select value={form.buildingId} disabled={onCart} onChange={(e) => setForm((c) => ({ ...c, buildingId: e.target.value, roomId: '' }))}>
                <option value="">No building</option>
                {buildingOptions.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                    {building.kind === 'WAREHOUSE' ? ' (warehouse)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Room">
              <Select value={form.roomId} onChange={set('roomId')} disabled={onCart || !form.buildingId}>
                <option value="">No room</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-500 uppercase">Purchasing</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Purchase date">
              <Input type="date" value={form.purchaseDate} onChange={set('purchaseDate')} />
            </Field>
            <Field label="Purchase price">
              <Input inputMode="decimal" value={form.purchasePrice} onChange={set('purchasePrice')} placeholder="0.00" />
            </Field>
            <Field label="Warranty expires">
              <Input type="date" value={form.warrantyExpiresAt} onChange={set('warrantyExpiresAt')} />
            </Field>
            <Field label="Vendor">
              <Input value={form.vendor} onChange={set('vendor')} maxLength={120} />
            </Field>
            <Field label="PO number">
              <Input value={form.poNumber} onChange={set('poNumber')} maxLength={120} />
            </Field>
            <Field label="Funding source">
              <Input value={form.fundingSource} onChange={set('fundingSource')} maxLength={120} placeholder="E-Rate, Title IV…" />
            </Field>
          </div>
        </section>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={set('notes')} maxLength={5000} rows={3} />
        </Field>
      </form>
    </Modal>
  );
}
